import React, { useMemo, useCallback, useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import _, { isUndefined, map, some } from 'lodash';
import { Button, Table, Input, Form as FormV4 } from 'antd';
import {Form as FormV5} from "antd-v5";
import { PlusOutlined } from '@ant-design/icons'
import { DeleteIcon, CheckIcon, CircleCrossIcon, EditIcon as OldEditIcon } from 'components/Icons'
import AuthorisedContainer from 'components/AuthorisedContainer';
import classNames from 'classnames';
import ACDropdown from 'ac-components/ACDropdown';
import ACButton from 'ac-components/ac-button/ACButton';
import { PermissionSetMap } from 'constants/RBACConstants';
import ACTable from 'ac-components/ACTable/ACTable';
import TrashIcon from 'ac-components/ac-icons/TrashIcon';
import SaveIcon from 'ac-components/ac-icons/SaveIcon';
import EditIcon from 'ac-components/ac-icons/EditIcon';
import PlusIcon from 'ac-components/ac-icons/PlusIcon';
import EllipsisVerticalIcon from 'ac-components/ac-icons/EllipsisVerticalIcon';
import { useAutoFocus } from 'hooks/useFocusHelpers';
import ACResizableColumnCell from "ac-components/ac-resizable-column-cell/ACResizableColumnCell";
import useTableCustomizationHelper from "hooks/useTableCustomizationHelper";


const TableWithAddDeleteActionColumn = forwardRef(({
  parentForm,
  columns,
  permission,
  source,
  editable = false,
  onChange = _.noop,
  mandatoryFirstRow = false,
  field,
  fieldRow,
  resetOnCancel,
  initialValue,
  idPrefix = '',
  newUI = false,
  addButtonText,
  onDataSourceChange,
  prefKey,
  ...props
}, ref) => {

  const [dataSource, setDataSource] = useState([]);
  const [columnKeys] = useState(() => _.map(columns, "dataIndex"));
  
  // Conditionally used Ant Design v5 Form for the new UI,
  // since the v4 Form does not maintain a reference to the parent v5 Form.
  const Form = newUI ? FormV5 : FormV4;
  const FormItem = newUI ? FormV5.Item : FormV4.Item;
  const [form] = Form.useForm(parentForm);
  const attrInputFieldRef = useAutoFocus([dataSource.length]);

  useImperativeHandle(ref, () => {
    return {
      getData: () => dataSource
    }
  }, [dataSource]);

  useEffect(() => {
    onDataSourceChange && onDataSourceChange(dataSource);
  }, [dataSource, onDataSourceChange])

  useEffect(() => {
    const dataSource = _.map(source, (tag, i) => {
      return { ...tag, id: `${idPrefix}_${i}` };
    });

    setDataSource(dataSource);
  }, [idPrefix, source]);

  const onAdd = () => {
    setDataSource(([...source]) => {
      const newEntry = {
        newRecordAdded: true,
        isEdit: true,
        ...initialValue,
      };

      _.forEach(columnKeys, key => {
        if(isUndefined(newEntry[key])){
          newEntry[key] = "";
        }
      });

      const lastRecord = _.last(source)
      const newId = lastRecord ? +(lastRecord.id.split("_").pop()) + 1 : 0;
      newEntry.id = `${idPrefix}_${newId}`;
      source.push(newEntry);
      return source;
    })
  }

  const clearFormDataOnRemove = useCallback((source, deleteIndex) => {
    const remainingRowsLength = source.length;
    const formData = form.getFieldsValue();

    // Shift form data to one index up from deleteIndex and clear last index
    for (let i = deleteIndex; i <= remainingRowsLength; i++) {
      _.forEach(columnKeys, key => {
        const currentFieldKey = fieldRow ? ['filter-action-row', fieldRow.name, 'actions', field.name, "value", `${key}_${i}`] : `${key}_${i}`;
        const nextFieldKey = fieldRow ? ['filter-action-row', fieldRow.name, 'actions', field.name, "value", `${key}_${i + 1}`] : `${key}_${i + 1}`;

        _.set(formData, currentFieldKey, i === remainingRowsLength ? undefined : _.get(formData, nextFieldKey));
      });
    }

    form.setFieldsValue(formData);
  }, [columnKeys, field?.name, fieldRow, form]);

  const toggleEdit = useCallback((rowIndex, edit = false) => {
    setDataSource(([...source]) => {
      const record = source[rowIndex];
      if (record.newRecordAdded) {
        source.splice(rowIndex, 1);
        clearFormDataOnRemove(source, rowIndex);
      } else {
        record.isEdit = edit;
      }
      return source;
    })
}, [clearFormDataOnRemove]);

  const onSave = useCallback(async (rowIndex) => {
    let currentFieldKeys = [];
    const columnKeysWithRow = _.map(columnKeys, key => `${key}_${rowIndex}`);
    if (fieldRow) {
      currentFieldKeys = _.map(columnKeys, key => ['filter-action-row', fieldRow.name, 'actions', field.name, "value", `${key}_${rowIndex}`]);
    } else {
      currentFieldKeys = columnKeysWithRow;
    }

    await form.validateFields(currentFieldKeys);
    setDataSource(([...source]) => {
      const record = source[rowIndex];
      let data = {};
      const formData = form.getFieldsValue();

      if (fieldRow) {
        data = _.get(formData, ['filter-action-row', fieldRow.name, 'actions', field.name, 'value']);
      } else {
        data = formData;
      }

      _.forEach(columnKeysWithRow, key => {
        const value = data[key]
        record[key.split("_")[0]] = _.isString(value) ? value.trim() : value;
      });

      record.isEdit = false;
      record.newRecordAdded = false;
      onChange(source);
      return source;
    });
  }, [columnKeys, field?.name, fieldRow, form, onChange]);

  const onDelete = useCallback((rowIndex) => {
    setDataSource(([...source]) => {
      const deleted = source.splice(rowIndex, 1);
      clearFormDataOnRemove(source, rowIndex);
      onChange(source, deleted);
      return source;
    });
  }, [clearFormDataOnRemove, onChange]);

  const isRowEditMode = some(dataSource, (row) => row.isEdit)

  const actionRender = useCallback((currentRecord) => {
    if (!editable) return null;

    const rowIndex = _.findIndex(dataSource, (record) => record.id === currentRecord.id);
    const shouldDisableCancelOrDelete = dataSource.length === 1 && mandatoryFirstRow;
    const fieldNames = map(columns, column => `${column.dataIndex}_${rowIndex}`);

    if (currentRecord.isEdit) {
      return (
        <>
          <AuthorisedContainer permission={permission} tooltip="Save">
            <ACButton
              onClick={() => onSave(rowIndex)}
              iconOnly
              type="outline"
              size="sm"
              style={{ marginRight: 8 }}
            >
              <SaveIcon />
            </ACButton>
          </AuthorisedContainer>
          <AuthorisedContainer permission={permission} tooltip="Cancel Edit">
            <ACButton
              disabled={shouldDisableCancelOrDelete}
              className={classNames("m-r-xs clickable", {
                disabled: shouldDisableCancelOrDelete,
              })}
              onClick={
                shouldDisableCancelOrDelete
                  ? _.noop
                  : () => {
                      if (resetOnCancel) {
                        form.resetFields(fieldNames);
                      }
                      toggleEdit(rowIndex, false);
                    }
              }
              iconOnly
              size="sm"
              type="danger"
            >
              <TrashIcon />
            </ACButton>
          </AuthorisedContainer>
        </>
      );
    }

    return (
      <ACDropdown
        menuWidth={120}
        items={[
          { label: 'Edit', icon: <EditIcon />, value: 'edit', disabled: PermissionSetMap[permission] },
          { label: <span className='danger'>Delete</span>, icon: <TrashIcon className='danger' />, value: 'delete', disabled: PermissionSetMap[permission] },
        ]}
        onOptionClick={key => {
          if (key === 'edit') toggleEdit(rowIndex, true);
          if (key === 'delete') onDelete(rowIndex);
        }}
        placement="bottomRight"
      >
        <ACButton type="ghost" iconOnly size="sm" className="m-l-xs" disabled={isRowEditMode}>
          <EllipsisVerticalIcon />
        </ACButton>
      </ACDropdown>
    );
  }, [columns, dataSource, editable, form, isRowEditMode, mandatoryFirstRow, onDelete, onSave, permission, resetOnCancel, toggleEdit]);

  const _columns = useMemo(() => [
    ...(_.map(columns, column => {
      let rules = column.rules || [];
      if (column.required) {
        rules = [
          {
            required: true,
            message: <>{column.title} is required.</>
          },
          ...rules
        ];
      }
      rules = [
        ...rules,
        ...(column.validator ? [{
          validator: (record, value) => column.validator(record, value, dataSource)
        }] : [])
      ];

      return {
        ...column,
        render: (value, currentRecord) => {
          if (currentRecord.isEdit) {
            // This is needed in case of sorting, The row gets sort but the row index always starts from 0
            // This calculates the record index from the array it resides
            const rowIndex = _.findIndex(dataSource, (record) => record.id === currentRecord.id);
            const inputProps = {};
            if (column.dataIndex === "columnName" && rowIndex === dataSource.length - 1) {
              inputProps.ref = attrInputFieldRef;
            }
            return (
              <FormItem
                initialValue={value}
                className={column.required && !newUI ? 'required-mark' : ''}
                name={field ? [field.name, "value", `${column.dataIndex}_${rowIndex}`] : `${column.dataIndex}_${rowIndex}`}
                style={{ width: "calc(100% - 10px)", marginBottom: 0, whiteSpace: 'initial' }}
                rules={rules}
              >
                {column.field ||
                  <Input size='small' {...inputProps} />}
              </FormItem>
            );
          }
          return column.render ? column.render(value) : value;
        }
      }
    })),
    ...(!newUI && editable ? [{
      title: "Actions",
      align: "center",
      key: "actions",
      dataIndex: "actions",
      width: "18%",
      fixed: "right",
      render: (value, currentRecord) => {
        // This is needed in case of sorting, The row gets sort but the row index always starts from 0
        // This calculates the record index from the array it resides
        const rowIndex = _.findIndex(dataSource, (record) => record.id === currentRecord.id);
        const shouldDisableCancelOrDelete = dataSource.length === 1 && mandatoryFirstRow;
        const fieldNames = map(columns, column => `${column.dataIndex}_${rowIndex}`)

        if(currentRecord.isEdit) {
        return (<>
              <AuthorisedContainer permission={permission} tooltip='Cancel Edit' key="cancel">
                <CircleCrossIcon
                  disabled={shouldDisableCancelOrDelete}
                  className={classNames('m-r-xs clickable', { disabled: shouldDisableCancelOrDelete })}
                  onClick={shouldDisableCancelOrDelete ? _.noop : () => {
                    if(resetOnCancel){
                      form.resetFields(fieldNames);
                    }
                    toggleEdit(rowIndex, false);
                  }}
                />
              </AuthorisedContainer>
              <AuthorisedContainer permission={permission} tooltip='Save' key="save">
                <CheckIcon className='clickable' onClick={() => onSave(rowIndex)} />
              </AuthorisedContainer>
            </>
          )}

            return (<>
              <AuthorisedContainer permission={permission} tooltip='Delete' key="delete">
                <DeleteIcon
                  disabled={shouldDisableCancelOrDelete}
                  className={classNames('m-r-xs clickable', { disabled: shouldDisableCancelOrDelete })}
                  onClick={shouldDisableCancelOrDelete ? _.noop : () => onDelete(rowIndex)} />
              </AuthorisedContainer>
              <AuthorisedContainer permission={permission} tooltip='Edit' key="edit">
                <OldEditIcon className='clickable' onClick={() => toggleEdit(rowIndex, true)} />
              </AuthorisedContainer>
            </>)
      },
    }] : [])

  ], [columns, editable, dataSource, permission, toggleEdit, onSave, onDelete, mandatoryFirstRow, field, resetOnCancel, form, newUI, attrInputFieldRef]);
  const { columns: customizedColumns } = useTableCustomizationHelper({
    columns: _columns,
    prefKey,
    skipColumnVisibility: true,
    draggable: false,
  });

  const getColumn = newUI ? customizedColumns : _columns;

   if (newUI) {
      return (
        <div>
        <ACTable
          fullPage
          dataSource={dataSource}
          columns={getColumn}
          pagination={false}
          rowKey={"id"}
          actionRender={editable ? actionRender : undefined}
          actionColumnWidth={60}
          footer={() => editable && (
            <AuthorisedContainer permission={permission}>
              <ACButton
                disabled={props.maxRows === dataSource.length || isRowEditMode}
                size="sm"
                type="outline"
                leftIcon={<PlusIcon />}
                onClick={onAdd}
              >
                {addButtonText ? addButtonText : "Add New"}
              </ACButton>
            </AuthorisedContainer>
          )}
          components={{
            header: {
              cell: ACResizableColumnCell
            }
          }}
          autoEnableScroll
          computeTableWidth
        />
        </div>
      );
    }


  return (
    <div>
      <div className='key-value-table p-sm p-t-0' style={{ background: '#fff' }}>
        <Table
          dataSource={dataSource}
          columns={_columns}
          size={"small"}
          pagination={false}
          rowKey='id'
        />
        {
          editable && (
            <div className='m-t-sm m-l-0 d-flex'>
              <div style={{ width: '100%' }} className='text-right'>
                <AuthorisedContainer permission={permission}>
                  <Button disabled={props.maxRows === dataSource.length} size='small' type="dashed" icon={<PlusOutlined />} onClick={onAdd}>
                    Add New
                  </Button>
                </AuthorisedContainer>
              </div>
            </div>
          )
        }
      </div>
    </div>
  );
});

export default TableWithAddDeleteActionColumn;
